/*    
<summary>
   This class component is all about Managing staff data functionality.
   Developer:Aashish Singh, Created Date:29-August-2023
</summary>
<param>No Parameter Passed</param>
<returns>Returns class instance</returns>
*/
import { action, computed, makeObservable, observable } from "mobx";
import toast from "react-hot-toast";
import { IObservableInitialState } from "../../models/ICommon";
import IApiResponse, {
  IApiSuccessResponse,
} from "../../models/response/IApiResponse";
import { ICommonState } from "../../models/state/ICommonState";
import { formatMessage } from "../../translations/format-message";
import { initialState as GetAllStaffManagementState } from "../initialState/get-all-staff-state";
import * as baseService from "../service/base-service";
import { AuthStore } from "./auth-store";

import URLConstants from "../../constants/url-constants";
import {
  getBusinessTypeIdFromLocalStorage,
  getLanguageFromLocalStorage,
  getStoreIdFromLocalStorage,
  getUserTypeFromLocalStorage,
} from "../../helpers/localstorage-helper";
import {
  IStaffManagementList,
  IStaffManagementListVM,
  IStaffManagementVM,
} from "../../models/response/IStaffManagementResponse";

import base64 from "base-64";
import userTypeEnum from "../../constants/enums/user-type-enum";
import { objectToFormData } from "../../helpers/file-manager-helper";
import {
  IAddStaffManagement,
  IUpdateStaffManagement,
} from "../../models/forms/IAddStaff";
import { IStaffFilter, IStaffStoreFilter } from "../../models/IFilter";
import {
  staffInitialFilterState,
  staffStoreInitialFilterState,
} from "../initialState/initial-filter-state";
import { AddStaffState } from "../initialState/add-edit-staff-state";
import { safeBase64Encode } from "../../constants/constants";

const authStore = new AuthStore();

export class StaffManagementStore implements ICommonState {
  inProgress = false;
  error = "";
  staffList: IStaffManagementListVM = GetAllStaffManagementState;

  initialStateValue: IObservableInitialState = {
    success: false,
    error: "",
    inProgress: false,
  };

  addUpdateStaffManagementState = { ...this.initialStateValue };
  deleteStaffManagementState = { ...this.initialStateValue };
  checkIfStaffAlreadyExistInStoreState = { ...this.initialStateValue };

  activeInactiveState = { ...this.initialStateValue };
  staffEmailCheckState = { ...this.initialStateValue };

  staff: IUpdateStaffManagement = { ...AddStaffState };
  staffEmailData: IUpdateStaffManagement = { ...AddStaffState };
  staffState = { ...this.initialStateValue };
  staffEmailState = { ...this.initialStateValue };

  allStaffList: IStaffManagementList[] = [];

  staffFilter: IStaffFilter = { ...staffInitialFilterState };
  staffStoreFilter: IStaffStoreFilter = { ...staffStoreInitialFilterState };
  isEmailCheckedOrFetched: boolean = false;
  emailDuplicacyConfirmedData: IAddStaffManagement = { ...AddStaffState };

  constructor() {
    makeObservable(this, {
      inProgress: observable,
      error: observable,
      staffList: observable,
      staff: observable,
      addUpdateStaffManagementState: observable,
      deleteStaffManagementState: observable,
      staffState: observable,
      staffFilter: observable,
      allStaffList: observable,
      staffEmailState: observable,
      staffStoreFilter: observable,
      staffEmailData: observable,
      isEmailCheckedOrFetched: observable,
      emailDuplicacyConfirmedData: observable,
      staffEmailCheckState: observable,
      setIsEmailCheckedOrFetched: action,
      GetCheckIfStaffAlreadyExistInStore: action,
      resetIsEmailCheckedOrFetched: action,
      setStaffStoreFilterDetail: action,
      setStaffEmailData: action,
      resetStaffEmailDetail: action,
      GetStaffManagementByEmailService: action,
      GetStaffManagementListService: action,
      GetStaffManagementFreewordFilterService: action,
      AddStaffManagementService: action,
      UpdateStaffManagementService: action,
      UpdateStaffManagementStatusService: action,
      DeleteStaffManagementService: action,
      GetStaffManagementByIdService: action,
      GetAllStaffManagementByStoreIdService: action,
      setStaffFilterDetail: action,
      reset: action,
      resetStaff: action,
      resetStaffManagementDetail: action,
      resetAddUpdateStaffManagementState: action,
      resetStaffFilterDetail: action,
      resetStaffStoreFilterDetail: action,
      allStaffManagement: computed,
      staffDetail: computed,
      getStaffFilter: computed,
      getAllStaffManagementPrefecture: computed,
      allStaffStoreList: computed,
      getStaffStoreFilter: computed,
      getIsEmailCheckedOrFetched: computed,
      getEmailDuplicacyConfirmedData: computed,
    });
  }

  /**
   * This function is used to Get All StaffManagements List data with pagination information by calling an API.
   * @param currentPage : Current Page Number
   * @param pagerSize : Page Size
   * @returns
   */
  GetStaffManagementListService = (
    currentPage: number,
    pagerSize: number,
    orderBy: string,
    column: string,
    filter: any
  ) => {
    this.inProgress = true;
    let storeId = -1;
    let businessTypeId = -1;
    const loggedInUserType = getUserTypeFromLocalStorage();
    if (userTypeEnum[userTypeEnum.Store] === loggedInUserType) {
      storeId = getStoreIdFromLocalStorage();
      businessTypeId = getBusinessTypeIdFromLocalStorage();
    } else {
      storeId = this.staffFilter.StoreId ?? -1;
    }

    if (userTypeEnum[userTypeEnum.HeadQuarter] === loggedInUserType) {
      businessTypeId = getBusinessTypeIdFromLocalStorage() ?? -1;
    }

    const urlParams = new URLSearchParams();
    urlParams.append("PageNo", String(currentPage));
    urlParams.append("PageSize", String(pagerSize));
    urlParams.append("staffName", String(filter.StaffName ?? ""));
    urlParams.append("OrderBy", orderBy);
    urlParams.append("Column", column);
    urlParams.append("is_invalid", String(filter.IsInvalid));
    urlParams.append("PrefectureName", String(filter.PrefectureName ?? ""));

    if (storeId > 0) {
      urlParams.append("storeId", String(storeId));
    }
    if (businessTypeId > 0) {
      urlParams.append("businessTypeId", String(businessTypeId));
    }

    const url =
      URLConstants.GetStaffManagementFilter + "?" + urlParams.toString();
    // this.staffList = demoStaffData;
    return baseService
      .getRequest(url)
      .then(
        (
          response: IApiResponse<IApiSuccessResponse<IStaffManagementListVM>>
        ) => {
          if (response.data.Error) {
            this.error = response.data.Message;
            toast.error(formatMessage(response.data.Message));
          } else {
            this.staffList = response.data.Data;
          }
        }
      )
      .catch((err: string) => {
        this.error = err;
      })
      .finally(
        action(() => {
          this.inProgress = false;
        })
      );
  };

  GetStaffManagementFreewordFilterService = (
    currentPage: number,
    pagerSize: number,
    searchText: string
  ) => {
    this.inProgress = true;

    let storeId = -1;
    let businessTypeId = -1;
    const loggedInUserType = getUserTypeFromLocalStorage();
    if (userTypeEnum[userTypeEnum.Store] === loggedInUserType) {
      storeId = getStoreIdFromLocalStorage();
      businessTypeId = getBusinessTypeIdFromLocalStorage();
    } else {
      storeId = this.staffFilter.StoreId ?? -1;
    }

    if (userTypeEnum[userTypeEnum.HeadQuarter] === loggedInUserType) {
      businessTypeId = getBusinessTypeIdFromLocalStorage() ?? -1;
    }

    const urlParams = new URLSearchParams();
    urlParams.append("PageNo", String(currentPage));
    urlParams.append("PageSize", String(pagerSize));
    urlParams.append("Search", String(searchText));
    if (businessTypeId > 0) {
      urlParams.append("businessTypeId", String(businessTypeId));
    }
    if (storeId > 0) {
      urlParams.append("storeId", String(storeId));
    }

    const url = URLConstants.StaffSearchFilter + "?" + urlParams.toString();
    // this.storeList = demoStoreData;
    return baseService
      .getRequest(url)
      .then(
        (
          response: IApiResponse<IApiSuccessResponse<IStaffManagementListVM>>
        ) => {
          if (response.data.Error) {
            this.error = response.data.Message;
            toast.error(formatMessage(response.data.Message));
          } else {
            this.staffList = response.data.Data;
          }
        }
      )
      .catch((err: string) => {
        this.error = err;
      })
      .finally(
        action(() => {
          this.inProgress = false;
        })
      );
  };

  /**
   * This function is used to map staffList to allStaffManagement List suitable for Grid Component.
   * @returns allStaffManagementsList
   */
  get allStaffManagement(): IStaffManagementList[] {
    if (this.staffList?.StaffVM && this.staffList.StaffVM?.length > 0)
      return this.staffList.StaffVM.map((staff) => {
        let language = getLanguageFromLocalStorage();
        let isDelete = true;
        let isEdit = true;
        return {
          Id: staff.Id,
          StoreId: staff.StoreId,
          StaffName: staff.StaffName,
          StoreName: staff.StoreName,
          TelephoneNumber: staff.TelephoneNumber,
          Email: staff.Email,
          Language: language,
          PostCode: staff.PostCode,
          AccountStatus: staff.AccountStatus,
          Address: staff.Address
            ? staff?.PrefectureName
              ? staff?.PrefectureName + staff.Address
              : staff.Address
            : "",
          PrefectureName: staff.PrefectureName,
          NumberOfAvailability: staff.NumberOfAvailability,
          NominationFee:
            typeof staff.NominationFee === "string"
              ? parseFloat(staff.NominationFee)
              : staff.NominationFee,
          Image: staff.Image,
          isDelete: isDelete,
          isEdit: isEdit,
          OrderId: staff.OrderId,
          Gender: staff.Gender?.toString(),
          Expertise: staff.Expertise,
          Introduction: staff.Introduction,
          OrderIdPersist: staff.OrderId,
          TotalCount: staff.TotalCount,
          ShowOnReservationSite: staff.ShowOnReservationSite,
        };
      });
    return [];
  }

  get getAllStaffManagementPrefecture(): any[] {
    if (this.staffList?.StaffVM && this.staffList.StaffVM?.length > 0) {
      // Create a Set to store unique prefecture names
      const uniquePrefectures = new Set<string>();

      // Iterate over the staffList and add prefecture names to the Set
      this.staffList.StaffVM.forEach((store) => {
        if (store?.PrefectureName) {
          uniquePrefectures.add(store.PrefectureName);
        }
      });

      // Convert the Set to an array of objects with id and value
      return Array.from(uniquePrefectures).map((prefecture, index) => ({
        id: index + 1, // Generate a unique ID (or use any other logic for ID generation)
        value: prefecture,
      }));
    }

    return [];
  }

  /**
   * This function is used to Get StaffManagement Details by Id by calling an API.
   * @param id : The StaffManagement Identifier
   * @returns
   */
  GetStaffManagementByIdService = (id: number, storeId: number) => {
    // let staffDetail = demoStaffData.StaffVM.filter(
    //   (staff: any) => staff.Id === id
    // );
    // if (staffDetail.length > 0) {
    //   this.staff = staffDetail[0];
    // }
    this.staffState.inProgress = true;
    let url = `${URLConstants.GetStaffManagementById}/${id}?storeId=${storeId}`;
    return baseService
      .getRequest(url)
      .then(
        (response: IApiResponse<IApiSuccessResponse<IStaffManagementVM>>) => {
          if (response.data.Error) {
            this.staffState.error = response.data.Message;
          } else {
            let data = response.data.Data;
            this.staff = data;
          }
        }
      )
      .catch((err: string) => {
        this.staffState.error = err;
      })
      .finally(
        action(() => {
          this.staffState.inProgress = false;
        })
      );
  };

  /**
   * This function provides initail values to the Add StaffManagement Form.
   * @returns StaffManagement Details
   *
   */
  get staffDetail(): IUpdateStaffManagement {
    const loggedInUserType = getUserTypeFromLocalStorage();
    if (this.staff) {
      return {
        ...AddStaffState,
        Id: this.staff.Id || AddStaffState.Id,
        StoreId:
          this.staff.StoreId > 0
            ? this.staff.StoreId
            : loggedInUserType === userTypeEnum[userTypeEnum.Store]
            ? Number(getStoreIdFromLocalStorage())
            : -1,
        StaffName:
          this.staff.StaffName ||
          this.staff.UserName ||
          AddStaffState.StaffName,
        TelephoneNumber:
          this.staff.TelephoneNumber || AddStaffState.TelephoneNumber,
        Email: this.staff.Email.toLowerCase() || AddStaffState.Email,
        PostCode: this.staff.PostCode || AddStaffState.PostCode,
        AccountStatus: this.staff.AccountStatus || AddStaffState.AccountStatus,
        NumberOfAvailability:
          this.staff.NumberOfAvailability || AddStaffState.NumberOfAvailability,
        NominationFee: this.staff.NominationFee || AddStaffState.NominationFee,
        Address: this.staff.Address
          ? this.staff?.PrefectureName
            ? this.staff?.PrefectureName +
              AddStaffState.PrefectureName +
              this.staff.Address
            : this.staff.Address
          : AddStaffState.Address,
        Image: this.staff.Image || AddStaffState.Image,
        PrefectureName:
          this.staff.PrefectureName || AddStaffState.PrefectureName,
        OrderId: this.staff.OrderId || AddStaffState.OrderId,
        Gender: this.staff.Gender?.toString() || AddStaffState.Gender,
        Expertise: this.staff.Expertise || AddStaffState.Expertise,
        Introduction: this.staff.Introduction || AddStaffState.Introduction,
        OrderIdPersist: this.staff.OrderId || AddStaffState.OrderIdPersist,
        TotalCount: this.staff.TotalCount || AddStaffState.TotalCount,
        IsOtherStaffEmail: this.staff.EmailUserType === userTypeEnum.Staff ? true : false,
        ShowOnReservationSite: this.staff.ShowOnReservationSite.toString() || AddStaffState.ShowOnReservationSite.toString(),
      };
    }
    return {
      ...AddStaffState,
      StoreId:
        loggedInUserType === userTypeEnum[userTypeEnum.Store]
          ? Number(getStoreIdFromLocalStorage())
          : -1,
    };
  }

  /*
    This function is used to reset all staff observables to their initial values.  
    */
  resetStaffManagementDetail = () => {
    this.staff = { ...AddStaffState };
    this.staffEmailData = { ...AddStaffState };
    this.staffState = { ...this.initialStateValue };
    this.staffEmailState = { ...this.initialStateValue };
  };

  resetStaffEmailDetail = () => {
    this.staffEmailState = { ...this.initialStateValue };
  };

  GetStaffManagementByEmailService = (email: string, storeId?: number) => {
    this.staffEmailState.inProgress = true;
    let StoreId = storeId ? storeId : getStoreIdFromLocalStorage();
    let buisnessTypeId = getBusinessTypeIdFromLocalStorage();
    let url = `${URLConstants.GetStaffManagementByEmail}?Email=${email}&storeId=${StoreId}&buisnessTypeId=${buisnessTypeId}`;
    return baseService
      .getRequest(url)
      .then(
        (response: IApiResponse<IApiSuccessResponse<any>>) => {
          if (response.data.Error) {
            this.staffEmailState.error = response.data.Message;
            toast.error(formatMessage(response.data.Message));
            this.staffEmailState.inProgress = false;
          } else {
            let data = response.data.Data;
            this.isEmailCheckedOrFetched = true;
            this.staffEmailData = storeId ? { ...this.staffEmailData, ...data, EmailUserType: data.UserType, StoreId: storeId } : { ...this.staffEmailData, ...data, EmailUserType: data.UserType };
            this.staffEmailState.success = true;
            this.staffEmailState.inProgress = false;
          }
        }
      )
      .catch((err: string) => {
        this.staffEmailState.error = err;
        toast.error(formatMessage(err));
        this.staffEmailState.inProgress = false;
      })
      .finally(
        action(() => {
          this.staffEmailState.inProgress = false;
        })
      );
  };

  setStaffEmailData = () => {
    this.staff = { ...this.staff, ...this.staffEmailData };
  };

  /**
   * This function is used to Add New StaffManagement by calling an API.
   * @param data : StaffManagement Data
   * @returns
   */
  AddStaffManagementService = (data: IAddStaffManagement, isStoreCredentialUsed: boolean) => {
    this.addUpdateStaffManagementState.inProgress = true;
    let url = URLConstants.AddStaffManagement;
    let staffData: any = {
      StoreId: data.StoreId,
      UserName: data.StaffName,
      Email: data.Email.toLowerCase(),
      UserType: 2,
      Password: safeBase64Encode(data.Password!),
      AccountStatus: data.AccountStatus,
      TelephoneNumber: String(data.TelephoneNumber),
      PostCode: data.PostCode,
      PrefectureName: data.PrefectureName,
      NumberOfAvailability: data.NumberOfAvailability,
      NominationFee: data.NominationFee,
      Address: data.Address,
      Image: data.Image,
      Gender: data.Gender,
      Expertise: data.Expertise,
      Introduction: data.Introduction,
      IsOtherStaffEmail: data.IsOtherStaffEmail,
      ShowOnReservationSite: data.ShowOnReservationSite,
      IsStoreCredentialUsed: isStoreCredentialUsed
    };

    const formData = objectToFormData(staffData);
    // const formData = new FormData();
    // formData.append("StoreId", data.StoreId);
    return baseService
      .postMultipart(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateStaffManagementState.error = response.data.Message;
        } else {
          this.addUpdateStaffManagementState.success = true;
        }
      })
      .catch((err: string) => {
        this.addUpdateStaffManagementState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateStaffManagementState.inProgress = false;
        })
      );
  };

  /**
   * This function is used to Updating Existing StaffManagement Details by calling an API.
   * @param id : The StaffManagement Identifier
   * @param data
   * @returns
   */
  UpdateStaffManagementService = (id: number, data: IAddStaffManagement) => {
    this.addUpdateStaffManagementState.inProgress = true;
    let url = URLConstants.UpdateStaffManagement;
    let staffData: any = {
      Id: id,
      StoreId: data.StoreId,
      UserName: data.StaffName,
      Email: data.Email.toLowerCase(),
      UserType: 2,
      AccountStatus: data.AccountStatus,
      TelephoneNumber: String(data.TelephoneNumber),
      PostCode: data.PostCode,
      NumberOfAvailability: data.NumberOfAvailability,
      PrefectureName: data.PrefectureName,
      NominationFee: data.NominationFee,
      Address: data.Address,
      Image: data.Image,
      OrderId: data.OrderId,
      Gender: data.Gender,
      Expertise: data.Expertise,
      Introduction: data.Introduction,
      IsOrderUpdated:
        Number(data.OrderId) === Number(data.OrderIdPersist) ? false : true,
      ShowOnReservationSite: data.ShowOnReservationSite,
    };
    const formData = objectToFormData(staffData);
    return baseService
      .putMultipart(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateStaffManagementState.error = response.data.Message;
        } else this.addUpdateStaffManagementState.success = true;
      })
      .catch((err: string) => {
        this.addUpdateStaffManagementState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateStaffManagementState.inProgress = false;
        })
      );
  };

  setIsEmailCheckedOrFetched = (value: boolean) => {
    this.isEmailCheckedOrFetched = value;
  };
  /*
    This function is used to reset all AddStaffManagement observables to their initial values.  
    */
  resetAddUpdateStaffManagementState = () => {
    this.addUpdateStaffManagementState.inProgress = false;
    this.addUpdateStaffManagementState.success = false;
    this.addUpdateStaffManagementState.error = "";
  };

  /**
   * This function is used to Delete StaffManagement by calling an API.
   * @param id : The StaffManagement Identifier
   * @returns
   */
  DeleteStaffManagementService = (id: number, storeId: number) => {
    this.deleteStaffManagementState.inProgress = true;
    let url = `${URLConstants.DeleteStaffManagement}/${id}?storeId=${storeId}`;
    return baseService
      .deleteRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.deleteStaffManagementState.error = response.data.Message;
          // toast.error(formatMessage(response.data.Message));
        } else this.deleteStaffManagementState.success = true;
      })
      .catch((err: string) => {
        this.deleteStaffManagementState.error = err;
      })
      .finally(
        action(() => {
          this.deleteStaffManagementState.inProgress = false;
        })
      );
  };

  /*
    This function is used to reset all AddStaffManagement observables to their initial values.  
    */
  resetDeleteStaffManagement = () => {
    this.deleteStaffManagementState.inProgress = false;
    this.deleteStaffManagementState.success = false;
    this.deleteStaffManagementState.error = "";
  };

  /**
   * This function is used to Updating Existing StaffManagement state (i.e. active/inactive) Details by calling an API.
   * @param id : The StaffManagement Identifier
   * @param state: StaffManagement state
   * @returns
   */
  UpdateStaffManagementStatusService = (id: number, state: boolean) => {
    this.activeInactiveState.inProgress = true;
    let url = URLConstants.UpdateStaffManagement;
    let staffData: any = {
      Id: id,
      IsActive: state,
    };
    return baseService
      .putRequest(url, staffData)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.activeInactiveState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else this.activeInactiveState.success = true;
      })
      .catch((err: string) => {
        this.activeInactiveState.error = err;
      })
      .finally(
        action(() => {
          this.activeInactiveState.inProgress = false;
        })
      );
  };

  /*
    This function is used to set the filter details to the Filter observable.  
    */
  setStaffFilterDetail = (filter: IStaffFilter) => {
    this.staffFilter = { ...filter };
  };

  resetStaffFilterDetail = () => {
    this.staffFilter = { ...staffInitialFilterState };
  };

  resetIsEmailCheckedOrFetched = () => {
    this.isEmailCheckedOrFetched = false;
    this.staffEmailCheckState.success = false;
  };
  get getIsEmailCheckedOrFetched() {
    return this.isEmailCheckedOrFetched;
  };

  get getStaffFilter(): IStaffFilter {
    return this.staffFilter;
  }

  setStaffStoreFilterDetail = (filter: IStaffStoreFilter) => {
    this.staffStoreFilter = { ...filter };
  };

  resetStaffStoreFilterDetail = () => {
    this.staffStoreFilter = { ...staffStoreInitialFilterState };
  };

  get getStaffStoreFilter(): IStaffStoreFilter {
    return this.staffStoreFilter;
  }

  GetAllStaffManagementByStoreIdService = (storeId: number, isCustomerLogin = false) => {
    this.staffState.inProgress = true;
    let url = `${URLConstants.GetAllStaffByStoreId}?storeId=${storeId}&isCustomerLogin=${isCustomerLogin}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.staffState.error = response.data.Message;
        } else {
          let data = response.data.Data;
          this.allStaffList = data;
        }
      })
      .catch((err: string) => {
        this.staffState.error = err;
      })
      .finally(
        action(() => {
          this.staffState.inProgress = false;
        })
      );
  };

  get allStaffStoreList(): IStaffManagementList[] {
    if (this.allStaffList && this.allStaffList?.length > 0)
      return this.allStaffList?.map((staff) => {
        let language = getLanguageFromLocalStorage();
        let isDelete = true;
        let isEdit = true;
        return {
          Id: staff.Id,
          StoreId: staff.StoreId,
          StaffName: staff.StaffName,
          StoreName: staff.StoreName,
          TelephoneNumber: staff.TelephoneNumber,
          Email: staff.Email,
          Language: language,
          PostCode: staff.PostCode,
          AccountStatus: staff.AccountStatus,
          Address: staff.Address
            ? staff?.PrefectureName
              ? staff?.PrefectureName + staff.Address
              : staff.Address
            : "",
          PrefectureName: staff.PrefectureName,
          NumberOfAvailability: staff.NumberOfAvailability,
          NominationFee: staff.NominationFee,
          Image: staff.Image,
          isDelete: isDelete,
          isEdit: isEdit,
          OrderId: staff.OrderId,
          Gender: staff.Gender?.toString(),
          Expertise: staff.Expertise,
          Introduction: staff.Introduction,
          OrderIdPersist: staff.OrderId,
          TotalCount: staff.TotalCount,
          ShowOnReservationSite: staff.ShowOnReservationSite,
        };
      });
    return [];
  }

  GetCheckIfStaffAlreadyExistInStore = (
    data: any,
  ) => {
    this.checkIfStaffAlreadyExistInStoreState.inProgress = true;
    let url = `${URLConstants.CheckIFStaffAlreadyExistInStore}`;
    let responseData: any = {
      StoreId: data.StoreId,
      BusinessTypeId: getBusinessTypeIdFromLocalStorage(),
      Email: data.Email.toLowerCase(),
      UserType: userTypeEnum.Staff,
    };
    return baseService
      .postRequest(url, responseData)
      .then(
        (
          response: IApiResponse<IApiSuccessResponse<IStaffManagementListVM>>
        ) => {
          if (response.data.Error) {
            this.checkIfStaffAlreadyExistInStoreState.error = response.data.Message;
            if (!(data?.Id && data?.Id > 0 && "StaffEmailAlreadyExist" === response.data.Message)) {
              toast.error(formatMessage(response.data.Message));
            } else {
              this.UpdateStaffManagementService(data.Id, data);
            }
          } else {
            data?.Id && data?.Id > 0 ?
              this.UpdateStaffManagementService(data.Id, data) :
              this.AddStaffManagementService(data, false);
          }
          this.checkIfStaffAlreadyExistInStoreState.inProgress = false;
        }
      )
      .catch((err: string) => {
        this.checkIfStaffAlreadyExistInStoreState.error = err;
        this.staffEmailCheckState.success = true;
        this.isEmailCheckedOrFetched = true;
        this.emailDuplicacyConfirmedData = data;
      })
      .finally(
        action(() => {
          this.checkIfStaffAlreadyExistInStoreState.inProgress = false;
        })
      );
  };

  get getEmailDuplicacyConfirmedData(): IAddStaffManagement {
    return this.emailDuplicacyConfirmedData;
  }
  /*
    This function is used to reset all observables to their initial values.  
    */
  reset = () => {
    this.error = "";
    this.inProgress = false;
    this.addUpdateStaffManagementState.success = false;
    this.addUpdateStaffManagementState.error = "";
    this.deleteStaffManagementState = { ...this.initialStateValue };
    this.addUpdateStaffManagementState.inProgress = false;
    this.staffState = { ...this.initialStateValue };
  };

  /**
   * This function is used to reset all staff observables to their initial values.
   * @returns
   */
  resetStaff = () => {
    this.error = "";
    this.inProgress = false;
    this.staffList = GetAllStaffManagementState;

    this.addUpdateStaffManagementState = { ...this.initialStateValue };
    this.deleteStaffManagementState = { ...this.initialStateValue };
    this.staff = { ...AddStaffState };
    this.staffState = { ...this.initialStateValue };
  };
}

export default new StaffManagementStore();
